import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CloudDownload as ImportIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { get } from "../../utils/networkUtils";

/**
 * Component that checks if World Cup tournament exists.
 * If not, shows an overlay blocking access to the page.
 * If yes, renders children normally.
 */
const WorldCupCheck = ({ children }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [worldCupExists, setWorldCupExists] = useState(false);

  useEffect(() => {
    checkWorldCupExists();
  }, []);

  const checkWorldCupExists = async () => {
    try {
      setChecking(true);

      // Fetch all tournaments and check for World Cup
      const response = await get("/admin/tournament-calendar?page=1&limit=100");

      if (response?.data) {
        const tournaments =
          response.data.tournamentCalendars || response.data || [];

        const worldCup = tournaments.find(
          (t) =>
            t.competition_name?.toLowerCase().includes("world cup") ||
            t.competition_name?.toLowerCase().includes("fifa world")
        );

        if (worldCup) {
          setWorldCupExists(true);
        } else {
          setWorldCupExists(false);
        }
      }
    } catch (err) {
      console.error("Failed to check World Cup:", err);
      setWorldCupExists(false);
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!worldCupExists) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: "center",
            border: "2px solid #ed6c02",
          }}
        >
          <WarningIcon sx={{ fontSize: 64, color: "warning.main", mb: 2 }} />

          <Typography variant="h5" gutterBottom>
            World Cup Not Found
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            To access matchday management, you first need to import the World
            Cup tournament from OPTA.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              startIcon={<ImportIcon />}
              onClick={() => navigate("/dashboard/ecff/opta-import")}
            >
              Go to OPTA Import
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate("/dashboard/tournaments")}
            >
              View Tournaments
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // World Cup exists, render children
  return children;
};

export default WorldCupCheck;
